import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, map, Observable } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as jose from 'jose';
import { environment } from '../../environments/environment';

/**
 * Dashboard parameters (filters that can be passed to the dashboard)
 * These are the actual filter values
 * Note: Metabase uses American spelling (organization_id, not organisation_id)
 */
export interface DashboardParams {
  organization_id?: string | number; // Organization filter (American spelling)
  organisation_id?: string | number; // Kept for backwards compatibility
  organisation?: string; // Disabled - don't send
  message_date?: string; // Disabled - don't send
  [key: string]: any;
}

/**
 * Embedding parameters configuration
 * Maps to Metabase embedding settings:
 * - 'disabled': Parameter is hidden and cannot be set
 * - 'enabled': Parameter is editable by end-users (visible in UI)
 * - 'locked': Parameter is hidden but set programmatically
 * Note: Metabase uses American spelling (organization_id, not organisation_id)
 */
export interface EmbeddingParams {
  organization_id?: 'enabled' | 'disabled' | 'locked'; // American spelling (used by Metabase)
  organisation_id?: 'enabled' | 'disabled' | 'locked'; // Kept for backwards compatibility
  organisation?: 'enabled' | 'disabled' | 'locked';
  message_date?: 'enabled' | 'disabled' | 'locked';
  [key: string]: 'enabled' | 'disabled' | 'locked' | undefined;
}

/**
 * How the embedded dashboard looks. These go in the URL hash, outside the
 * signed token, so they never change what Metabase accepts.
 */
export interface EmbedDisplay {
  /** Metabase's own frame around the dashboard. Default true. */
  bordered?: boolean;
  /** Metabase's own dashboard title. Default true. */
  titled?: boolean;
  /** 'night' renders Metabase's dark theme. */
  theme?: 'night' | null;
}

/** "#bordered=false&titled=false&theme=night" */
export function embedHash(display: EmbedDisplay = {}): string {
  const parts = [`bordered=${display.bordered ?? true}`, `titled=${display.titled ?? true}`];
  if (display.theme) parts.push(`theme=${display.theme}`);
  return `#${parts.join('&')}`;
}

interface MetabaseConfig {
  siteUrl: string;
  secretKey: string;
  tokenExpirationMinutes: number;
}

@Injectable({
  providedIn: 'root',
})
export class MetabaseService {
  private metabaseUrl = environment.metabase.instanceUrl;
  private metabaseSecretKey = environment.metabase.secretKey; // Should be stored securely on backend

  // ⚠️ WARNING: Secret key exposed in frontend!
  private readonly config: MetabaseConfig = {
    siteUrl: environment.metabase.instanceUrl,
    secretKey: environment.metabase.secretKey, // ⚠️ EXPOSED TO EVERYONE!
    tokenExpirationMinutes: 10,
  };

  /**
   * Default embedding parameters matching your Metabase configuration
   * Using 'locked' so the parameter is set programmatically but hidden from users
   * Note: Metabase uses American spelling (organization_id, not organisation_id)
   */
  private readonly defaultEmbeddingParams: EmbeddingParams = {
    organization_id: 'locked',
  };

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
  ) {}

  /**
   * Generate JWT token for Metabase dashboard
   * FIXED: Ensures complete payload structure
   */
  /**
   * Generate JWT token for Metabase dashboard
   * ✅ FIXED: Explicitly add iat claim
   */
  private async generateToken(
    dashboardId: number,
    params: DashboardParams = {},
    embeddingParams?: EmbeddingParams,
    secretKeyOverride?: string,
  ): Promise<string> {
    try {
      const secret = new TextEncoder().encode(secretKeyOverride ?? this.config.secretKey);

      const finalEmbeddingParams = {
        ...this.defaultEmbeddingParams,
        ...embeddingParams,
      };

      const filteredParams = this.filterParams(params, finalEmbeddingParams);

      // ✅ Calculate iat (issued at) in seconds
      const iat = Math.floor(Date.now() / 1000);

      // ✅ Build complete payload with iat explicitly included
      const payload = {
        resource: {
          dashboard: dashboardId,
        },
        params: filteredParams || {},
        iat: iat, // ✅ Explicitly add iat
        _embedding_params: finalEmbeddingParams,
      };


      // ✅ Generate token with payload that includes iat
      const token = await new jose.SignJWT(payload).setProtectedHeader({ alg: 'HS256' }).sign(secret);


      await this.verifyToken(token, secretKeyOverride);

      return token;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Filter parameters based on embedding configuration
   */
  private filterParams(params: DashboardParams, embeddingParams: EmbeddingParams): DashboardParams {
    const filtered: DashboardParams = {};

    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const paramState = embeddingParams[key];

        // Only include if parameter is enabled or locked (not disabled)
        if (paramState === 'enabled' || paramState === 'locked') {
          filtered[key] = params[key];
        } else if (paramState === 'disabled') {
        } else {
          // If not in embedding config, include it (backward compatibility)
          filtered[key] = params[key];
        }
      }
    }

    return filtered;
  }

  /**
   * Verify token structure (for debugging)
   */
  private async verifyToken(token: string, secretKeyOverride?: string): Promise<void> {
    try {
      const secret = new TextEncoder().encode(secretKeyOverride ?? this.config.secretKey);
      const result = await jose.jwtVerify(token, secret);
      const payload = result.payload as Record<string, unknown>;


      // Validate required fields using safe checks
      if (!('resource' in payload)) {
      }
      if (!('params' in payload)) {
      }
      if (!('_embedding_params' in payload)) {
      }
      if (!('iat' in payload)) {
      }

      // Log token structure (cast to any for property access)
      const anyPayload = payload as any;
    } catch (error) {
    }
  }

  /**
   * Get embed URL for a Metabase dashboard
   */
  getEmbedUrl(
    dashboardId: number,
    params: DashboardParams = {},
    embeddingParams?: EmbeddingParams,
    override?: { instanceUrl?: string; secretKey?: string },
    display?: EmbedDisplay,
  ): Observable<string> {
    return from(this.generateToken(dashboardId, params, embeddingParams, override?.secretKey)).pipe(
      map(token => {
        const siteUrl = override?.instanceUrl ?? this.config.siteUrl;
        const url = `${siteUrl}/embed/dashboard/${token}${embedHash(display)}`;
        return url;
      }),
    );
  }

  /**
   * Get safe embed URL for iframe
   */
  getSafeEmbedUrl(
    dashboardId: number,
    params: DashboardParams = {},
    embeddingParams?: EmbeddingParams,
    override?: { instanceUrl?: string; secretKey?: string },
    display?: EmbedDisplay,
  ): Observable<SafeResourceUrl> {
    return this.getEmbedUrl(dashboardId, params, embeddingParams, override, display).pipe(
      map(url => this.sanitizer.bypassSecurityTrustResourceUrl(url)),
    );
  }

  /**
   * Convert a UUID string to base64-encoded bytes for MongoDB BinData comparison.
   * The USSD dashboard filters by organisation_id stored as Binary subtype 0.
   * e.g. '11e27d33-a104-4c01-9b39-1f3dbb822fe4' → 'EeJ9M6EETAGbOR89u4Iv5A=='
   */
  uuidToBase64(uuid: string): string {
    const hex = uuid.replace(/-/g, '');
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    }
    return btoa(String.fromCharCode(...bytes));
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<MetabaseConfig>): void {
    Object.assign(this.config, config);
  }
}
